import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import difference from 'lodash/difference';
import {PathDescription, PathRef} from "../models/Core";
import moment from "moment";
import {EventoObject} from "../models/Evento";
import {
  fbUseEmulator, fbEmulatorHost, fbEmulatorPort, fbEmulatorAuthPort, fbEmulatorAuthHost,
  fbEmulatorStoragePort, fbEmulatorStorageHost,
  frWebApiToken,
  fbAuthDomain, fbProjectId, fbStorageBucket, fbMessagingSenderId, fbAppId, fbMeasurementId,
} from "../config";


export const QueryOperatorInLimit = 10;

const isOffline = () => typeof navigator !== "undefined" && !navigator.onLine;

const firebaseConfig = {
  apiKey: frWebApiToken,
  authDomain: fbAuthDomain,
  projectId: fbProjectId,
  storageBucket: fbStorageBucket,
  messagingSenderId: fbMessagingSenderId,
  appId: fbAppId,
  measurementId: fbMeasurementId,
};

// console.log("Inicializando firebase...");
const app = firebase.initializeApp(firebaseConfig);

if (typeof window !== "undefined") {
  window.addEventListener("offline", async () => {
    try {
      await fb.db.disableNetwork();
      console.log("Firestore network disabled");
    } catch {}
  });

  window.addEventListener("online", async () => {
    try {
      await fb.db.enableNetwork();
      console.log("Firestore network enabled");
    } catch {}
  });
}

const getFirestore = (): firebase.firestore.Firestore => {
  const db = firebase.firestore();
  if (fbUseEmulator) {
    // console.log("USANDO EMULADOR FIREBASE");
    db.useEmulator(fbEmulatorHost, fbEmulatorPort);
  }

  return db;
};

const getAuth = () => {
  const auth = firebase.auth();
  auth.languageCode = 'pt';

  if (fbUseEmulator) {
    // console.log("USANDO EMULADOR FIREBASE AUTH");
    auth.useEmulator(`http://${fbEmulatorAuthHost}:${fbEmulatorAuthPort}`);
  }

  return auth;
};

const getStorage = () => {
  const storage = firebase.storage();

  if (fbUseEmulator)
    storage.useEmulator(fbEmulatorStorageHost, fbEmulatorStoragePort);

  return storage;
};

const fb = {
  firebase: firebase,
  app: app,
  auth: getAuth(),
  db: getFirestore(),
  files: getStorage(),
  vars: {
    camposControle: ['criado_em', 'atualizado_em'],
  },


  utils: {
    path: {
      descriptionToRef: (desc: PathDescription): PathRef => {
        return {
          label: desc.label,
          path: fb.db.collection(desc.collection).doc(desc.id),
        };
      },
    }
  },

  doc: (snap:firebase.firestore.DocumentSnapshot): any | undefined => {
    if (!snap.exists)
      return undefined;

    return {
      ...snap.data(),
      id: snap.id,
      path: snap.ref.path,
      type: snap.ref.parent.id,
    };
  },

  col: (snap:firebase.firestore.QuerySnapshot): any[] => {
    const colecao: any[] = [];
    snap.forEach(d => {
      if (d.exists)
        colecao.push(fb.doc(d));
    });
    return colecao;
  },

  first: (snap:firebase.firestore.QuerySnapshot): any | undefined => {
    const res = fb.col(snap);
    if (res.length > 0)
      return res[0];

    return undefined;
  },

  document: {
    create: async (
      collection: string,
      values: any,
      path?: string
    ): Promise<firebase.firestore.DocumentReference> => {

      const doc = path
        ? fb.db.collection(collection).doc(path)
        : fb.db.collection(collection).doc();

      const payload = {
        ...values,
        criado_em: fb.firebase.firestore.FieldValue.serverTimestamp(),
        atualizado_em: fb.firebase.firestore.FieldValue.serverTimestamp(),
      };

      if (isOffline()) {
        // dispara mas NÃO espera backend
        doc.set(payload).catch(() => {});
        return doc;
      }

      await doc.set(payload);
      return doc;
    },

    update: async (
      collection: string,
      path: string,
      values: any,
      removeFields: boolean = false
    ): Promise<firebase.firestore.DocumentReference> => {

      const doc = fb.db.collection(collection).doc(path);

      const updateValues: any = {
        ...values,
        atualizado_em: fb.firebase.firestore.FieldValue.serverTimestamp(),
      };

      if (removeFields) {
        const snap = await doc.get();
        const camposAtuais = Object.keys(snap.data() as object);
        const camposNovos = Object.keys(values as object);
        const dif = difference(camposAtuais, [
          ...camposNovos,
          ...fb.vars.camposControle,
        ]);

        dif.forEach(i => {
          updateValues[i] = fb.firebase.firestore.FieldValue.delete();
        });
      }

      if (isOffline()) {
        doc.update(updateValues).catch(() => {});
        return doc;
      }

      await doc.update(updateValues);
      return doc;
    },
  },

  anexos: {
    label: (evento:EventoObject): string => {
      return `${moment(evento.data.toDate()).format('DD/MM/YYYY')}: (${evento.tipo}) ${evento.descricao}`;
    },
  },

  eventos: {
    label: (evento:EventoObject): string => {
      return `${moment(evento.data.toDate()).format('DD/MM/YYYY')}: (${evento.tipo}) ${evento.descricao}`;
    },
  },
};

export default fb;
